import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Trash2, Info, CheckCircle2 } from 'lucide-react';

export type ConfirmDialogType = 'danger' | 'warning' | 'info' | 'success';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmDialogType;
  itemName?: string;
  isLoading?: boolean;
  variant?: 'default' | 'minimal';
}

export function ConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  itemName,
  isLoading = false,
}: ConfirmDialogProps) {
  const variant = (arguments[0] as any).variant ?? 'default';
  const handleConfirm = async () => {
    await onConfirm();
    onOpenChange(false);
  };

  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <AlertTriangle className="h-6 w-6 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      case 'info':
        return <Info className="h-6 w-6 text-blue-600" />;
      case 'success':
        return <CheckCircle2 className="h-6 w-6 text-green-600" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
    }
  };

  const getActionButtonClass = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-600';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-600';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-600';
      case 'success':
        return 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-600';
      default:
        return 'bg-gray-800 hover:bg-gray-900 text-white focus:ring-gray-800';
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {variant === 'minimal' ? (
        <AlertDialogContent className="max-w-md rounded-lg border p-5">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-gray-900">
              {title}
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-1 text-sm text-gray-600">
              {description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {type === 'danger' && (
            <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 text-xs text-red-800">
              <strong className="font-semibold">Warning:</strong> This action cannot be undone. All associated data will be permanently removed.
            </div>
          )}
          <AlertDialogFooter className="gap-3 mt-4 w-full grid grid-cols-2">
            <AlertDialogCancel 
              disabled={isLoading}
              className="hover:bg-gray-100 transition-colors rounded-md px-4 py-2 text-sm w-full"
            >
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={isLoading}
              className={`${getActionButtonClass()} rounded-md px-4 py-2 text-sm w-full ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      ) : (
        <AlertDialogContent className="max-w-sm p-4">
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 p-3 bg-red-100 mt-0.5 shadow-sm">{getIcon()}</div>
              <div className="flex-1">
                <AlertDialogTitle className="text-lg font-semibold text-gray-900 mb-1">
                  {title}
                </AlertDialogTitle>
                {itemName && (
                  <div className="mt-2 px-3 py-2 bg-gray-100 text-sm font-medium text-gray-900 border-l-4 border-gray-800">
                    {itemName}
                  </div>
                )}
                <AlertDialogDescription className="mt-2 text-sm text-gray-700 leading-relaxed">
                  {description}
                </AlertDialogDescription>
                {type === 'danger' && (
                  <div className="mt-3 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-700" />
                    <p className="text-xs text-red-800 leading-relaxed">
                      <strong className="font-bold">Warning:</strong> This action cannot be undone. All associated data will be permanently removed.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-3">
            <AlertDialogCancel 
              disabled={isLoading}
              className="hover:bg-gray-100 transition-all duration-200 px-4 py-2 text-sm"
            >
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={isLoading}
              className={`${getActionButtonClass()} transition-all duration-200 px-4 py-2 text-sm ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <span className="mr-2">Processing...</span>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </>
              ) : (
                confirmText
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}

// Hook for easier usage
export function useConfirmDialog() {
  const [dialogState, setDialogState] = React.useState<{
    open: boolean;
    title: string;
    description: string;
    type: ConfirmDialogType;
    itemName?: string;
    onConfirm: () => void | Promise<void>;
    confirmText?: string;
    cancelText?: string;
  }>({
    open: false,
    title: '',
    description: '',
    type: 'warning',
    onConfirm: () => {},
  });

  const [isLoading, setIsLoading] = React.useState(false);

  const confirm = React.useCallback(
    ({
      title,
      description,
      type = 'warning',
      itemName,
      confirmText,
      cancelText,
    }: {
      title: string;
      description: string;
      type?: ConfirmDialogType;
      itemName?: string;
      confirmText?: string;
      cancelText?: string;
    }) => {
      return new Promise<boolean>((resolve) => {
        setDialogState({
          open: true,
          title,
          description,
          type,
          itemName,
          confirmText,
          cancelText,
          onConfirm: async () => {
            setIsLoading(true);
            try {
              resolve(true);
            } finally {
              setIsLoading(false);
              setDialogState((prev) => ({ ...prev, open: false }));
            }
          },
        });
      });
    },
    []
  );

  const ConfirmDialogComponent = React.useMemo(
    () => (
      <ConfirmDialog
        {...dialogState}
        isLoading={isLoading}
        onOpenChange={(open) =>
          setDialogState((prev) => ({ ...prev, open }))
        }
      />
    ),
    [dialogState, isLoading]
  );

  return { confirm, ConfirmDialog: ConfirmDialogComponent };
}
